import { useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { Modal, Input, Textarea } from '../components/UI'
import toast from 'react-hot-toast'

export default function GeneralResources() {
  const { user } = useAuth()
  const isOwner = user?.role === 'owner'
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [expanded, setExpanded] = useState({})

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const all = await api.getResources()
      setResources(all.filter(r => r.section === 'general'))
    } catch (err) { toast.error(err.message) }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  function openAdd() { setForm({ section:'general', title:'', note:'', link:'' }); setModal('add') }
  function openEdit(r) { setForm({ ...r }); setModal(r) }
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  async function save() {
    if (!form.title) { toast.error('Title required'); return }
    setSaving(true)
    try {
      if (modal === 'add') await api.createResource({ ...form, section:'general' })
      else await api.updateResource(modal.id, { ...form, section:'general' })
      toast.success('Saved!'); setModal(null); load()
    } catch (err) { toast.error(err.message) }
    setSaving(false)
  }

  async function del(r) {
    if (!window.confirm(`Delete "${r.title}"?`)) return
    try { await api.deleteResource(r.id); toast.success('Deleted'); load() }
    catch (err) { toast.error(err.message) }
  }

  if (loading) return (
    <div style={{padding:40,color:'var(--text2)',fontFamily:'DM Mono,monospace',fontSize:13,display:'flex',alignItems:'center',gap:12}}>
      <div style={{width:16,height:16,border:'2px solid var(--border2)',borderTopColor:'var(--accent)',borderRadius:'50%',animation:'spin 0.7s linear infinite'}}/>
      Loading...
    </div>
  )

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'}}>
      <div className="toolbar">
        <div className="toolbar-left">
          <span className="toolbar-title">📦 General Resources</span>
          <span className="toolbar-sub">{resources.length} items</span>
        </div>
        {isOwner && <button className="btn btn-primary" onClick={openAdd}>+ Add Resource</button>}
      </div>

      <div style={{flex:1,overflowY:'auto',padding:20}}>
        <div style={{maxWidth:800}}>
          {resources.length === 0 ? (
            <div style={{textAlign:'center',padding:'60px 20px',color:'var(--text3)'}}>
              <div style={{fontSize:36,marginBottom:10}}>📦</div>
              <div style={{fontSize:14,fontWeight:600}}>No general resources yet</div>
              <div style={{fontSize:12,marginTop:4}}>{isOwner ? 'Click "Add Resource" to create one' : 'Nothing here yet'}</div>
            </div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {resources.map(r => {
                const isOpen = expanded[r.id]
                return (
                  <div key={r.id} style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:10,overflow:'hidden'}}>
                    <div style={{display:'flex',alignItems:'center',gap:10,padding:'12px 14px',cursor: r.note ? 'pointer' : 'default'}}
                      onClick={() => r.note && setExpanded(p => ({...p, [r.id]: !p[r.id]}))}>
                      <div style={{flex:1}}>
                        <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>{r.title}</div>
                        {r.note && !isOpen && (
                          <div style={{fontSize:12,color:'var(--text3)',marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:500}}>
                            {r.note.slice(0,90)}{r.note.length>90?'…':''}
                          </div>
                        )}
                      </div>
                      {r.link && (
                        <a href={r.link} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()}
                          className="btn btn-secondary btn-sm" style={{textDecoration:'none',whiteSpace:'nowrap'}}>🔗 Open Link</a>
                      )}
                      {r.note && <span style={{fontSize:12,color:'var(--text3)'}}>{isOpen ? '▲' : '▼'}</span>}
                      {isOwner && (
                        <>
                          <button className="btn btn-secondary btn-sm" onClick={e=>{e.stopPropagation();openEdit(r)}}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={e=>{e.stopPropagation();del(r)}}>Del</button>
                        </>
                      )}
                    </div>
                    {isOpen && r.note && (
                      <div style={{padding:'0 14px 14px 14px',fontSize:13,color:'var(--text2)',lineHeight:1.7,whiteSpace:'pre-wrap',borderTop:'1px solid var(--border)',paddingTop:12}}>
                        {r.note}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {modal && (
        <Modal title={modal==='add'?'Add General Resource':'Edit Resource'} onClose={()=>setModal(null)}
          footer={<><button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'Save'}</button></>}>
          <Input label="Title" value={form.title||''} onChange={set('title')} placeholder="e.g. Company Operations Manual" />
          <Textarea label="Note / Instructions (optional)" value={form.note||''} onChange={set('note')} style={{minHeight:160}} placeholder="Write detailed instructions here..." />
          <Input label="Link (optional)" type="url" value={form.link||''} onChange={set('link')} placeholder="https://..." />
          <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>Visible to the owner and anyone granted General Resources permission.</div>
        </Modal>
      )}
    </div>
  )
}
